import { useState } from 'react';
import { UI, SPIRIT, QUOTES, STORIES } from './i18n';
import {
  useMarketStats,
  fmtUsd,
  fmtNum,
  fmtPct,
  fmtPrice,
} from './useMarketStats';
import { usePriceFeed } from './usePriceFeed';
import Sparkline from './Sparkline';
import LangSwitcher from './LangSwitcher';

const PRICE_FEED_PROJECT_ID = 4;

const logo = `${process.env.PUBLIC_URL}/logo.jpg`;
const CA = '0x234c3bFC260277cb4497E498A83aB685E30Ac730';
const PAIR = '0xa6c24770d62b156115198de3fcc9be9e70bf975e';
const DEX_URL = `https://dexscreener.com/bsc/${PAIR}`;
const DEX_EMBED = `https://dexscreener.com/bsc/${PAIR}?embed=1&theme=light&info=0&trades=0`;
const SWAP_URL = `https://pancakeswap.finance/swap?outputCurrency=${CA}`;

const shortenAddress = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;

export default function Home({ lang, setLang }) {
  const [copied, setCopied] = useState(false);
  const stats = useMarketStats({ pair: PAIR, contract: CA });
  const feed = usePriceFeed({ projectId: PRICE_FEED_PROJECT_ID, timeframe: 'hour' });

  // Prefer real-time WS feed for chart price + 24h move; fall back to DexScreener while WS is connecting.
  const priceUsd = feed.priceUsd ?? stats.priceUsd;
  const priceChange24h = feed.priceChange24h ?? stats.priceChange24h;
  const sparkData = feed.bars
    .map((b) => parseFloat(b.close))
    .filter(Number.isFinite);

  const t = (obj) => (obj ? obj[lang] ?? obj.en ?? obj.zh : '');

  const onCopy = () => {
    navigator.clipboard?.writeText(CA);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const onLogoClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  return (
    <>
      {/* ============ NAV ============ */}
      <nav className="nav">
        <div className="container nav-inner">
          <a href="#top" className="nav-brand" onClick={onLogoClick}>
            <img src={logo} alt="ALLIN" />
            <span>ALLIN</span>
          </a>
          <div className="nav-links">
            <a href="#spirit">{t(UI.nav.spirit)}</a>
            <a href="#quotes">{t(UI.nav.quotes)}</a>
            <a href="#stories">{t(UI.nav.stories)}</a>
            <a href="#chart">{t(UI.nav.chart)}</a>
            <a href="#/manifesto" className="nav-link-alt">
              {t(UI.manifesto.homeLink)}
            </a>
          </div>
          <div className="nav-right">
            <LangSwitcher lang={lang} setLang={setLang} />
            <a
              href={SWAP_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-dark"
            >
              {t(UI.nav.buy)}
            </a>
          </div>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <header id="top" className="hero">
        <div className="hero-watermark" aria-hidden>
          <img src={logo} alt="" />
        </div>
        <div className="container">
          <div className="hero-tag">
            <span className="dot" />
            {t(UI.hero.tag)}
          </div>

          <div className="hero-stage">
            <div className="hero-text">
              <h1>
                {t(UI.hero.title1)}
                <br />
                <em>{t(UI.hero.title2)}</em>
              </h1>

              <p className="hero-lead">{t(UI.hero.lead)}</p>

              <div className="hero-cta">
                <a
                  href={SWAP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-dark"
                >
                  {t(UI.hero.buy)}
                </a>
                <a href="#stories" className="btn btn-ghost">
                  {t(UI.hero.readStories)}
                </a>
              </div>

              <div className="hero-ca">
                <div className="hero-ca-meta">
                  <span className="ca-tag">{t(UI.hero.network)}</span>
                  <span className="ca-label">{t(UI.hero.contractLabel)}</span>
                </div>
                <div className="hero-ca-row">
                  <code className="ca-full" title={CA}>
                    {CA}
                  </code>
                  <code className="ca-short" title={CA}>
                    {shortenAddress(CA)}
                  </code>
                  <button
                    onClick={onCopy}
                    className="ca-copy"
                    aria-label={t(UI.token.copy)}
                  >
                    {copied ? t(UI.token.copied) : t(UI.token.copy)}
                  </button>
                </div>
              </div>
            </div>

            <div className="hero-side">
              <div className="spark-card spark-card--hero">
                <div className="spark-head">
                  <div className="spark-pair">
                    <span className="spark-symbol">{t(UI.spark.pair)}</span>
                    <span className="spark-change-row">
                      <span className="spark-price">
                        {fmtPrice(priceUsd)}
                      </span>
                      <span
                        className={`spark-change ${
                          priceChange24h != null && priceChange24h < 0
                            ? 'down'
                            : 'up'
                        }`}
                      >
                        {fmtPct(priceChange24h) ?? '—'}
                      </span>
                    </span>
                  </div>
                  <div className="spark-range">{t(UI.spark.range)}</div>
                </div>
                <Sparkline
                  data={sparkData}
                  positive={priceChange24h == null || priceChange24h >= 0}
                />
              </div>

              <aside className="hero-thesis">
                <span className="eyebrow">{t(UI.hero.thesisLabel)}</span>
                <blockquote>{t(UI.hero.thesis)}</blockquote>
                <cite>{t(UI.hero.thesisCite)}</cite>
              </aside>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="stats">
            <div className="stats-grid">
              <div className="stat">
                <div className="eyebrow">{t(UI.stats.mcap)}</div>
                <div className="stat-num">{fmtUsd(stats.marketCap)}</div>
                <div
                  className={`stat-sub ${
                    priceChange24h != null && priceChange24h < 0
                      ? 'down'
                      : ''
                  }`}
                >
                  {stats.loading && priceChange24h == null
                    ? t(UI.stats.mcapDeltaWaiting)
                    : `${fmtPct(priceChange24h) ?? '—'} / ${t(
                        UI.stats.mcapDeltaSuffix
                      )}`}
                </div>
              </div>
              <div className="stat">
                <div className="eyebrow">{t(UI.stats.holders)}</div>
                <div className="stat-num">{fmtNum(stats.holders)}</div>
                <div className="stat-sub muted">{t(UI.stats.holdersSub)}</div>
              </div>
              <div className="stat">
                <div className="eyebrow">{t(UI.stats.volume)}</div>
                <div className="stat-num">{fmtUsd(stats.volume24h)}</div>
                <div className="stat-sub muted">{t(UI.stats.volumeSub)}</div>
              </div>
              <div className="stat">
                <div className="eyebrow">{t(UI.stats.liquidity)}</div>
                <div className="stat-num">{fmtNum(stats.liquidityQuote)}</div>
                <div className="stat-sub muted">{t(UI.stats.liquiditySub)}</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ============ MARQUEE ============ */}
      <div className="marquee" aria-hidden>
        <div className="marquee-track">
          {[0, 1].map((row) => (
            <span key={row}>
              {UI.marquee[lang].map((word, i) => (
                <span key={i}>
                  {word}
                  <span className="star">✦</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* ============ SPIRIT ============ */}
      <section id="spirit" className="section spirit">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">{t(UI.sections.ch1)}</span>
              <h2>
                {t(UI.sections.spiritT1)}
                <em>{t(UI.sections.spiritT2)}</em>
              </h2>
            </div>
            <p>{t(UI.sections.spiritDesc)}</p>
          </div>

          <div className="spirit-grid">
            {SPIRIT.map((s) => (
              <div key={s.n} className="spirit-item">
                <div className="spirit-num">{s.n}</div>
                <h3>{t(s.title)}</h3>
                <p>{t(s.desc)}</p>
              </div>
            ))}
          </div>

          <div className="spirit-readmore">
            <a href="#/manifesto" className="btn btn-gold">
              {t(UI.manifesto.homeLink)}
            </a>
          </div>
        </div>
      </section>

      {/* ============ QUOTES ============ */}
      <section id="quotes" className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">{t(UI.sections.ch2)}</span>
              <h2>
                {t(UI.sections.quotesT1)}
                <em>{t(UI.sections.quotesT2)}</em>
              </h2>
            </div>
            <p>{t(UI.sections.quotesDesc)}</p>
          </div>

          <div className="quotes">
            {QUOTES.map((q, i) => (
              <article
                key={i}
                className={`quote size-${q.size} ${
                  q.size === 'feature' ? 'feature' : ''
                }`}
              >
                <div className="quote-mark">"</div>
                <div className="quote-text">{t(q.text)}</div>
                <div className="quote-author">
                  <strong>{t(q.author)}</strong>
                  <span>{t(q.role)}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STORIES ============ */}
      <section id="stories" className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">{t(UI.sections.ch3)}</span>
              <h2>
                {t(UI.sections.storiesT1)}
                <em>{t(UI.sections.storiesT2)}</em>
              </h2>
            </div>
            <p>{t(UI.sections.storiesDesc)}</p>
          </div>

          <div className="stories">
            {STORIES.map((s, i) => (
              <article key={i} className="story">
                <div className="story-portrait" data-initial={s.initial}>
                  <img
                    src={`${process.env.PUBLIC_URL}/stories/${s.image}`}
                    alt={t(s.surname)}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <span className="portrait-fallback">
                    <em>{s.initial}</em>
                    <small>{t(s.surname)}</small>
                  </span>
                </div>
                <div className="story-body">
                  <div className="story-meta">
                    <span className="year">{s.year}</span>
                    <span>{t(s.tag)}</span>
                  </div>
                  <h3>{t(s.title)}</h3>
                  <p>{t(s.body)}</p>
                  <div className="story-line">
                    <span>{t(s.line)}</span>
                    <strong>{t(s.out)}</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CHART ============ */}
      <section id="chart" className="section chart-section">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">{t(UI.sections.ch4)}</span>
              <h2>
                {t(UI.sections.chartT1)}
                <em>{t(UI.sections.chartT2)}</em>
              </h2>
            </div>
            <p>{t(UI.sections.chartDesc)}</p>
          </div>

          <div className="chart-frame">
            <div className="chart-toolbar">
              <a
                href={DEX_URL}
                target="_blank"
                rel="noreferrer"
                className="chart-link"
              >
                {t(UI.chart.openDex)}
              </a>
              <a
                href={SWAP_URL}
                target="_blank"
                rel="noreferrer"
                className="btn btn-gold"
              >
                {t(UI.chart.swap)}
              </a>
            </div>
            <iframe
              title="ALLIN / DexScreener"
              src={DEX_EMBED}
              className="chart-iframe"
              loading="lazy"
              allow="clipboard-write"
            />
          </div>
        </div>
      </section>

      {/* ============ CTA BIG ============ */}
      <section className="cta-big">
        <div className="container">
          <h2>
            {t(UI.cta.t1)}
            <em>{t(UI.cta.t2)}</em>
          </h2>
          <p>{t(UI.cta.desc)}</p>
          <div className="hero-cta">
            <a
              href={SWAP_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-dark"
            >
              {t(UI.cta.buy)}
            </a>
            <a
              href="https://t.me"
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost"
            >
              {t(UI.cta.community)}
            </a>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="footer">
        <div className="container">
          <div className="footer-inner">
            <div>
              <div className="footer-brand">
                <img src={logo} alt="ALLIN" />
                <span>ALLIN</span>
              </div>
              <p className="footer-tagline">{t(UI.footer.tagline)}</p>
            </div>
            <div className="footer-cols">
              <div className="footer-col">
                <h4>{t(UI.footer.protocol)}</h4>
                <a href="#chart">{t(UI.footer.chartLink)}</a>
                <a href="#spirit">{t(UI.footer.spiritLink)}</a>
                <a href="#/manifesto">{t(UI.manifesto.title)}</a>
              </div>
              <div className="footer-col">
                <h4>{t(UI.footer.community)}</h4>
                <a href="https://twitter.com" target="_blank" rel="noreferrer">
                  {t(UI.footer.twitter)}
                </a>
                <a href="https://t.me" target="_blank" rel="noreferrer">
                  {t(UI.footer.telegram)}
                </a>
                <a href="https://discord.com" target="_blank" rel="noreferrer">
                  {t(UI.footer.discord)}
                </a>
              </div>
              <div className="footer-col">
                <h4>{t(UI.footer.resources)}</h4>
                <a href="#stories">{t(UI.footer.storiesLink)}</a>
                <a href="#quotes">{t(UI.footer.quotesLink)}</a>
                <a href="#chart">{t(UI.footer.contractLink)}</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <span>{t(UI.footer.copyright)}</span>
            <span>{t(UI.footer.disclaimer)}</span>
          </div>
        </div>
      </footer>
    </>
  );
}
